import {
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { safeJSONParse } from "core/model/utils/strings";
import { PER_ITEM_ERRORS } from "core/schemas/GKVMedicalFormSchema";
import {
  Auction,
  DocsFormDataMapped,
  DocsFormName,
  QueryProgress,
  RehabFormName,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import { useWarnBeforeExit } from "dsl/hooks/useWarnBeforeExit";
import { useWarnBeforeNavigate } from "dsl/hooks/useWarnBeforeNavigate";
import { ReactNode, useState } from "react";
import { RehabFormFormGetters } from "./RehabForms/configs";
import { TransitionalCareFormGetters } from "./TransitionalCare/Form/Import/utils";

export function sanitize(value: unknown) {
  if (typeof value === "string") {
    return value.replace(/[\u202C\u200E\u200F\u202A-\u202E]/g, "").trim();
  }
  if (typeof value === "number") {
    const cleaned = String(value)
      .replace(/[\u202C\u200E\u200F\u202A-\u202E]/g, "")
      .trim();
    return isNaN(Number(cleaned)) ? null : Number(cleaned);
  }
  return value;
}

export function parsePerItemValidation(validation: any) {
  const infos = validation?.infos;

  const [_, parsed] = safeJSONParse<any>(infos ?? "");

  const perItemErrors: (string | null)[] =
    parsed?.type === PER_ITEM_ERRORS && Array.isArray(parsed.messages)
      ? parsed.messages
      : [];

  const baseValidation =
    typeof infos === "string" && !infos.includes(PER_ITEM_ERRORS)
      ? validation
      : undefined;

  return { baseValidation, perItemErrors };
}

export const useImportFormData = ({
  auction,
  formGetters,
  formOnChange,
  formValueGetter,
}: {
  auction: Auction;
  formGetters?:
    | TransitionalCareFormGetters
    | RehabFormFormGetters<RehabFormName>;
  formOnChange: (value: any, statePath: string, validation: any) => void;
  formValueGetter?: (statePath: string) => unknown;
}) => {
  const [queryProgress, setQueryProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const importFormData = () => {
    if (!formGetters) return;
    setQueryProgress(QUERY_PROGRESS_PENDING);
    Object.entries(formGetters).forEach(([field, fieldGetter]) => {
      if (!fieldGetter) return;
      const value = fieldGetter(auction);
      const sanitizedValue = sanitize(value);

      formOnChange(sanitizedValue ?? null, field, null);
    });
    setQueryProgress(QUERY_PROGRESS_SUCCEED);
    return;
  };

  function handleAuctionToFormData<T extends DocsFormName>(): {
    isOverwriting: boolean;
    formData: DocsFormDataMapped<T> | undefined;
  } {
    if (!formGetters) {
      return { formData: undefined, isOverwriting: false };
    }

    let isOverwriting = false;
    let formData: DocsFormDataMapped<T> | undefined;

    Object.entries(formGetters).forEach(([field, fieldGetter]) => {
      if (!fieldGetter) return;

      const value = fieldGetter(auction);
      const sanitizedValue = sanitize(value);

      if (formValueGetter) {
        const currentValue = formValueGetter?.(field);
        if (sanitizedValue && currentValue && currentValue !== sanitizedValue) {
          isOverwriting = true;
        }
      }

      if (sanitizedValue) {
        formData = {
          ...formData,
          [field]: sanitizedValue,
        };
      }
    });

    return { formData, isOverwriting };
  }

  return {
    handleAuctionToFormData,
    importFormData,
    importProgress: queryProgress,
  };
};

export const ExitPreventionWrapper = ({
  children,
  isDirty,
}: {
  children: ReactNode;
  isDirty: boolean;
}) => {
  useWarnBeforeExit({ skip: !isDirty });
  useWarnBeforeNavigate({
    skip: !isDirty,
  });

  return <>{children}</>;
};

export function assertUnreachable(x: never): never {
  throw new Error(`Unhandled case: ${x}`);
}

export const formatDate = (timestamp: number, locale: Locale) =>
  format(fromUnixTime(timestamp), "P", { locale });
